import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

// === GENERATE TASK ===
export function generateTask() {
  // k w {2..9} -> n = k^2
  const ks = [2, 3, 4, 5, 6, 7, 8, 9];
  const k = ks[Math.floor(Math.random() * ks.length)];
  const n = k * k;

  // Losujemy, czy mianownik to (x-k) czy (x+k)
  const denomIsMinus = Math.random() < 0.5;

  const numeratorTex = `x^2-${n}`;
  const denominatorTex = denomIsMinus ? `x-${k}` : `x+${k}`;

  // Wynik po skróceniu
  const expected = denomIsMinus ? `x+${k}` : `x-${k}`;

  // Dziedzina z mianownika
  // x-k ≠ 0 → x ≠ k
  // x+k ≠ 0 → x ≠ -k
  const domainTex = denomIsMinus
    ? `x\\neq ${k}`
    : `x\\neq -${k}`;

  return {
    type: "DiffSquaresOverSumOrDiffTask",
    data: {
      k,
      n,
      denomIsMinus,
      numeratorTex,
      denominatorTex,
      expected,
      domainTex,
    },
  };
}

// === RENDER TASK ===
export function renderTask({ data }) {
  const { numeratorTex, denominatorTex, domainTex } = data;

  return (
    <div className="space-y-2">
      <p>
        Uprość wyrażenie. Załóż, że <InlineMath math={domainTex} />.
      </p>
      <BlockMath math={`\\frac{${numeratorTex}}{${denominatorTex}}`} />
    </div>
  );
}

// === RENDER INPUT ===
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <EquationInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      placeholder={"np. x+2"}
      showPreview
    />
  );
}

// === CHECK ANSWER ===
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "string") {
    return { isCorrect: false, expected: data.expected };
  }

  // Akceptujemy przemienność dodawania (x+7 == 7+x),
  // ale NIE przemienność odejmowania
  const normalize = (str) =>
    str
      .replace(/−/g, "-")
      .replace(/\s+/g, "")
      .replace(/\*/g, "")
      .toLowerCase()
      .replace(/-/g, "+-")
      .split("+")
      .filter(Boolean)
      .sort()
      .join("+");

  return {
    isCorrect: normalize(userAnswer) === normalize(data.expected),
    expected: data.expected,
  };
}

// === RENDER ANSWER ===
export function renderAnswer({ data }) {
  return <InlineMath math={data.expected} />;
}

// === EXPLANATION ===
export function renderExplanation({ data }) {
  const { k, numeratorTex, denominatorTex, expected, domainTex } = data;

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Zauważ, że{" "}
        <InlineMath math={`${data.n}=${k}^2`} />, więc licznik jest różnicą
        kwadratów:
      </p>
      <BlockMath math={`x^2-${k}^2=(x-${k})(x+${k})`} />

      <p>
        <strong>Krok 2:</strong> Podstawiamy do ułamka i skracamy wspólny czynnik:
      </p>
      <BlockMath
        math={`\\frac{${numeratorTex}}{${denominatorTex}}
        =\\frac{(x-${k})(x+${k})}{${denominatorTex}}
        =${expected}`}
      />

      <p className="text-sm opacity-90">
        Warunek <InlineMath math={domainTex} /> wynika z tego, że mianownik nie
        może być równy zero.
      </p>
    </div>
  );
}

// === RENDER USER ANSWER ===
export function renderUserAnswer(...args) {
  const ua = args.length === 1 ? args[0] : args[1];
  const raw = typeof ua === "string" ? ua : ua?.raw;
  if (!raw || raw.trim() === "") return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={raw} />;
}
